/** @jsxImportSource @emotion/react */

import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import colors from '@sportninja/common/constants/appColors'
import { Spin, Table, Popconfirm, Alert } from 'antd'
import { Suspense, useMemo, useState } from 'react'
import Icon from 'src/components/Icon'
import { useSelector, useDispatch } from 'src/hooks/useRedux'
import accountActions from '@sportninja/common/actions/account'
import Text from 'src/components/ts-components/Text'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import TaxModal from 'src/components/TaxModal'
import { TaxData } from 'src/components/TaxModal/TaxContext'
import { getLocaleFromCountry } from '@sportninja/common/currency'

const columns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: 'Description',
    dataIndex: 'description',
    key: 'description',
  },
  {
    title: 'Tax Rate',
    dataIndex: 'tax_rate',
    key: 'tax_rate',
  },
  {
    title: 'Country',
    dataIndex: 'country',
    key: 'country',
  },
  {
    title: 'Options',
    dataIndex: 'options',
    key: 'options',
  },
]

export const Taxes = () => {
  const dispatch = useDispatch()
  const account = useSelector((state: any) => state.account._meAccount)
  const loading = useSelector((state: any) => state.account._meLoading)
  const user = useSelector((state: any) => state.auth.user)

  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [isTaxModalOpen, setIsTaxModalOpen] = useState(false)
  const [taxModalInitialData, setTaxModalInitialData] = useState<
    TaxData | undefined
  >(undefined)

  const dataSource = useMemo(() => {
    return account?.taxes.map((tax) => ({
      key: tax?.id,
      name: tax?.name,
      description: tax.description,
      tax_rate: (() => {
        const locale = getLocaleFromCountry(tax?.currency_country)
        const formatted = new Intl.NumberFormat(locale, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 4,
        }).format(Number(tax?.tax_rate) || 0)
        return `${formatted}%`
      })(),
      country: tax?.currency_country?.name || '—',
      options: (
        <div
          css={css`
            display: flex;
            gap: 16px;
          `}
        >
          <button
            onClick={() => {
              setTaxModalInitialData({
                id: tax?.id,
                name: tax?.name,
                description: tax?.description || '',
                tax_rate: tax?.tax_rate,
                currency_country_id: tax?.currency_country?.id,
              })
              setIsTaxModalOpen(true)
            }}
          >
            <Icon name='pen' color={colors.DEFAULT_FLAIR} fontSize={16} />
          </button>
          <Popconfirm
            title='Delete this tax?'
            description='Are you sure you want to delete this tax?'
            onConfirm={async () => {
              try {
                setDeleteError(null)
                await req(`/account/${account?.uid}/tax/${tax?.id}/`, {
                  method: 'DELETE',
                })
                dispatch(
                  accountActions.fetchAccount.request(
                    null,
                    null,
                    user?.account_id
                  )
                )
              } catch (e: any) {
                const errorMessage = getErrorMessage(e)
                setDeleteError(errorMessage)
              }
            }}
            okText='Yes'
            cancelText='No'
          >
            <button>
              <Icon name='trash' color={colors.ERROR} fontSize={16} />
            </button>
          </Popconfirm>
        </div>
      ),
    }))
  }, [account])

  return (
    <>
      <div
        css={css`
          background-color: ${colors.HEADER};
          padding: 16px;
          border-radius: 8px;
          border: 1px solid ${colors.SOFT_STEEL};
          margin: 0 auto;
          margin-top: 16px;
        `}
      >
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            <Text variant='heading4' weight='semiBold' color={colors.NEUTRAL_0}>
              TAXES
            </Text>
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              Set Up Tax Rates To Apply Charges During The Registration Process.
            </Text>
          </div>
          <button
            onClick={() => {
              setTaxModalInitialData(undefined)
              setDeleteError(null)
              setIsTaxModalOpen(true)
            }}
            css={css`
              display: flex;
              gap: 8px;
              align-items: center;
              color: ${colors.DEFAULT_FLAIR};
            `}
          >
            <p>Add</p>
            <Icon
              name='plus-square'
              color={colors.DEFAULT_FLAIR}
              fontSize={16}
            />
          </button>
        </div>

        {deleteError && (
          <Alert
            showIcon
            message={deleteError}
            type='error'
            css={css`
              margin-top: 16px;
            `}
          />
        )}

        <Suspense fallback={<Spin />}>
          <div
            css={css`
              margin-top: 16px;
              padding-top: 16px;
              border-top: 1px solid ${colors.SOFT_STEEL};

              * {
                border-color: ${colors.SOFT_STEEL} !important;
              }
            `}
          >
            <Table
              loading={loading}
              bordered
              pagination={false}
              columns={columns}
              dataSource={dataSource}
              locale={{
                emptyText: 'No Taxes Registered',
              }}
            />
          </div>
        </Suspense>
      </div>
      <TaxModal
        isOpen={isTaxModalOpen}
        onClose={() => setIsTaxModalOpen(false)}
        initialData={taxModalInitialData}
        accountUid={account?.uid || ''}
        onSuccess={() =>
          dispatch(
            accountActions.fetchAccount.request(null, null, user?.account_id)
          )
        }
        defaultCountryId={String(account?.country_id || '')}
      />
    </>
  )
}
